/**
 * A transient banner across the top of the garage: something just happened,
 * said once, and dismissed — by the player or by itself.
 *
 * ── Why this exists beside `state/reward-popup.ts` ───────────────────────────
 *
 * That module is a *receipt*: it holds focus behind a scrim and the caller
 * awaits its dismissal, because there was a decision — or at least a moment —
 * worth pausing the screen for. A daily-quest claim, pressed from a card on the
 * garage floor or a button inside the quests window while nothing else is
 * waiting on the answer, does not need a pause. It needs a confirmation the
 * player can read without losing whatever else is on screen — the quests
 * window, the hangar behind it, a shop tab. So this is deliberately the
 * opposite shape: fire-and-forget rather than awaited, non-blocking rather
 * than a scrim, and anchored under the title bar rather than the centre of the
 * screen, which is where a garage player's eye already is.
 *
 * ── Reusable by construction, not by intention ───────────────────────────────
 *
 * A notice is a title and a list of lines and almost nothing else — no
 * `questId`, no field either caller could use to ask for special treatment.
 * The daily-quest claim is the first caller; the shape does not know it
 * exists, exactly as `reward-popup.ts`'s `RewardReport` does not know about
 * quests.
 *
 * `kind` is the first deliberate exception, added when a deploy failure became
 * the second caller. A refusal to enter the arena is not a reward, and
 * painting it in the same amber a claimed quest wears would say the opposite
 * of what it means — so this is a single optional discriminator, not a
 * severity system: two values, `'success'` (the default, unstated) and
 * `'error'`, each read by `ui/NotificationBanner.vue` for its accent colour
 * and `aria-live` politeness. A third kind should earn its place the way this
 * one did, by a caller that could not honestly wear either.
 *
 * `stats` is the second, and it earned its place the same way. A spend used to
 * say only what it *cost* — `Upgraded` over `300 credits withdrawn` — which is
 * the half of a purchase the player already knew before they pressed the
 * button. What they bought was three numbers moving, and those were shown
 * nowhere: the card behind the banner redraws with the new figures, and there
 * is no *before* left anywhere on screen to compare them against. So a notice
 * may now carry a small before/after readout, and it is still not a channel a
 * caller can style through: the rows arrive **already formatted and already
 * scaled** — a label, two strings, two bar fractions and a direction — so this
 * component can draw them without knowing what a hull is, exactly as it renders
 * `lines` without knowing what a quest is. Everything that decides *which* stats
 * moved and *how far* a bar should sit lives in `state/garage-outcome.ts`.
 *
 * ── One at a time ─────────────────────────────────────────────────────────────
 *
 * A second notice while one is showing replaces it outright, the same choice
 * `reward-popup.ts` makes and for the same reason: nobody is waiting on the
 * first one's answer, so replacing it loses nothing but a glance.
 */
import { computed, ref } from 'vue';

/**
 * One stat moving, as the banner draws it.
 *
 * Pre-formatted on purpose — see the note above. `from`/`to` carry their unit
 * and their scaling already applied (`formatStat`), and the two fractions are
 * `fillFraction`'s answer for the same two values, so the bar and the figures
 * cannot come to different conclusions. `better` is the *player's* point of
 * view rather than the arithmetic's: a reload that drops is an improvement, and
 * a banner that painted it red would be the one place in the garage saying the
 * opposite of what the shop card says.
 */
export interface BannerStat {
  /** "Shell damage". */
  label: string;
  /** The value before the change, formatted with its unit. */
  from: string;
  /** The value after it. */
  to: string;
  /** 0-1, where the bar sat before. */
  fromFill: number;
  /** 0-1, where it sits now. */
  toFill: number;
  /** True when the move is an improvement for the player. */
  better: boolean;
}

export interface BannerNotice {
  /** The small label above the message: "Reward claimed". */
  title: string;
  /** One or more lines of detail, rendered in order, already formatted. */
  lines: string[];
  /**
   * Milliseconds before it dismisses itself. Omitted (or 0) leaves it up until
   * closed by hand — the close button is drawn either way, never only for the
   * manual case.
   */
  autoDismissMs?: number;
  /**
   * `'success'` (the default when omitted) or `'error'`. Purely a rendering
   * hint for `NotificationBanner.vue` — the accent colour and `aria-live`
   * politeness — and nothing here branches on it.
   */
  kind?: 'success' | 'error';
  /**
   * Before-and-after rows, drawn as bars under the lines. Omitted for the
   * notices that have no numbers to show, which is most of them.
   */
  stats?: BannerStat[];
  /**
   * One button beside the message — "Install" on an upgrade-ready notice. The
   * third exception to "a title and lines", earned by the first notice that
   * exists to send the player somewhere rather than to confirm something.
   * Pressing it runs `run` and closes the notice.
   */
  action?: { label: string; run: () => void };
}

interface Pending extends BannerNotice {
  id: number;
}

let nextId = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
const pending = ref<Pending | null>(null);

/**
 * The notice on screen, or `null`. Read by `ui/NotificationBanner.vue`.
 *
 * Rebuilt field by field rather than handed straight out, so the internal `id`
 * — which exists only to stop one notice's timer dismissing its successor —
 * never reaches the component. **Every field of `BannerNotice` has to be named
 * here or it silently does not exist**: a field added to the interface and to
 * the template but not to this list type-checks, renders nothing, and gives no
 * clue why. `notification-banner.test.ts` pins that.
 */
export const bannerNotice = computed<BannerNotice | null>(() =>
  pending.value
    ? {
        title: pending.value.title,
        lines: pending.value.lines,
        kind: pending.value.kind,
        stats: pending.value.stats,
        action: pending.value.action,
      }
    : null,
);

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * Shows a notice, replacing whatever was already showing.
 *
 * Never queues: see the "one at a time" note above.
 */
export function showNotice(notice: BannerNotice): void {
  clearTimer();
  const id = ++nextId;
  pending.value = { ...notice, id };
  if (notice.autoDismissMs && notice.autoDismissMs > 0) {
    timer = setTimeout(() => {
      // Only the notice this timer belongs to. A notice that replaced this one
      // must not be dismissed by the timer of the one it replaced.
      if (pending.value?.id === id) pending.value = null;
      timer = null;
    }, notice.autoDismissMs);
  }
}

/** Runs the notice's action, then closes it. */
export function runNoticeAction(): void {
  const action = pending.value?.action;
  dismissNotice();
  action?.run();
}

/** Closes the notice on screen, if any — the close button. */
export function dismissNotice(): void {
  clearTimer();
  pending.value = null;
}

/**
 * Test seam: clears state without depending on a timer firing.
 *
 * Mirrors `resetRewards()` — a suite that left a pending auto-dismiss timer
 * running between tests would fire it against whatever the next test set up.
 */
export function resetNotice(): void {
  clearTimer();
  pending.value = null;
}
